import { getBufferedRequestCount } from '@shared/extension';
import { captureSentryMessage, safeSentryCaptureException } from '@shared/sentry';
import { ONE_MINUTE_MS } from '@shared/utils/constants';
import { Mutex } from 'async-mutex';
import { ContentTooLargeError } from '../errors';
import { postToBackend } from './post-to-backend';
import { withRetry } from './with-retry';
import { LogLevel } from '@environment/enums/log-level.enum';

interface LastError {
  error: string;
  time: string;
}

const LAST_ERRORS_LOCAL_STORAGE_KEY = 'PostToBackendFromLocalStorage:LastErrors';
const REQUEST_AGE_THRESHOLD = ONE_MINUTE_MS * 10;
const RETRY_AFTER_MINUTES = 1;
const MAX_PAYLOAD_RESPONSE_LENGTH = 100;
const postToBackendMutex = new Mutex();

export async function postToBackendFromLocalStorage(key: string, backendUrl: string, source: string): Promise<void> {
  const release = await postToBackendMutex.acquire();
  try {
    const item = await chrome.storage.local.get(key);
    const payload = item[key];

    captureSentryMessage(
      `postToBackendFromLocalStorage ${key} to backend from source: ${source}..., url: ${payload?.request?.url}: pageUrl: ${payload?.pageUrl}`,
      { source, pageUrl: payload?.pageUrl, requestUrl: payload?.request?.url },
      LogLevel.Debug
    );
    
    if (!payload) {
      console.log(`Key ${key} exists but has no payload or is empty, removing key`);
      await chrome.storage.local.remove(key);
      captureSentryMessage(
        `postToBackendFromLocalStorage removed empty key ${key}`,
        { source },
        LogLevel.Debug
      );
      return;
    }

    // Only remove after successful posting
    await withRetry(async () => await postToBackend(backendUrl, payload, key));
    
    // Now safe to remove the key
    await chrome.storage.local.remove(key);
    
    captureSentryMessage(
      `postToBackendFromLocalStorage ${key} successfully removed from local storage`,
      { source },
      LogLevel.Debug
    );
    
    await checkRequestAge(key, payload);
  } catch (e) {
    await saveLastErrorInStorage(e);
    if (e instanceof ContentTooLargeError) {
      // For content too large, we should remove the key to avoid retrying
      await chrome.storage.local.remove(key);
      captureSentryMessage(
        `Removed oversized request ${key}: ${e.message}`,
        { source },
        LogLevel.Warning
      );
    } else {
      // TODO what if the request keeps failing? dropOldStoredRequests will remove it from local storage after 3 hours
      console.error(`Failed to send to backend: ${e}`);
      chrome.alarms.create(`retrySendToBackend`, { delayInMinutes: RETRY_AFTER_MINUTES });
      safeSentryCaptureException(new Error(`postToBackendFromLocalStorage failed for key ${key}: ${e as String}`));
    }
  } finally {
    release();
  }
}

async function saveLastErrorInStorage(e: unknown): Promise<void> {
  try {
    const lastErrorsData = await chrome.storage.local.get(LAST_ERRORS_LOCAL_STORAGE_KEY);
    const lastErrors: LastError[] = lastErrorsData[LAST_ERRORS_LOCAL_STORAGE_KEY] ?? [];
    let errorMessage = JSON.stringify(e);
    if (!errorMessage || errorMessage === '{}') {
      const error = e as Error;
      if (error.message) {
        errorMessage = error.message;
        if (error.stack) {
          errorMessage += `\n${error.stack}`;
        }
      } else {
        errorMessage = error.toString();
      }
    }
    lastErrors.unshift({ error: errorMessage, time: new Date().toISOString() });
    await chrome.storage.local.set({ [LAST_ERRORS_LOCAL_STORAGE_KEY]: lastErrors });
  } catch {
    console.error(`Failed to save last errors: ${e}`);
  }
}

async function checkRequestAge(key: string, payload: { response: unknown }): Promise<void> {
  try {
    const keyParts = key.split('::');
    const age = Date.now() - parseInt(keyParts[keyParts.length - 1]);
    if (age > REQUEST_AGE_THRESHOLD) {
      const lastErrorsData = await chrome.storage.local.get(LAST_ERRORS_LOCAL_STORAGE_KEY);
      const lastErrors: LastError[] = lastErrorsData[LAST_ERRORS_LOCAL_STORAGE_KEY] ?? [];
      let payloadString: string | undefined;
      try {
        if (payload && payload.response) {
          let payloadResponseString = JSON.stringify(payload.response);
          if (payloadResponseString.length > MAX_PAYLOAD_RESPONSE_LENGTH) {
            payloadResponseString = payloadResponseString.slice(0, MAX_PAYLOAD_RESPONSE_LENGTH);
          }
          payloadString = JSON.stringify({ ...payload, response: payloadResponseString });
        }
      } finally {
        payloadString = payloadString ?? JSON.stringify(payload);
      }
      const errorMessage = `Request ${key} is too old: ${age}ms, payload: ${payloadString}, lastErrors: ${JSON.stringify(
        lastErrors
      )}`;
      console.warn(errorMessage);
      captureSentryMessage(
        errorMessage,
        {
          totalPendingRequests: await getBufferedRequestCount(),
        },
        LogLevel.Debug
      );
    }
  } catch (e) {
    console.error(`Failed to check request age: ${e}`);
  } finally {
    await chrome.storage.local.remove(LAST_ERRORS_LOCAL_STORAGE_KEY);
  }
}
